import { useCallback, useEffect, useRef, useState } from "react"
import { useSanity } from "@hooks/useSanity"
import { useFunctions } from "@hooks/useFunctions"
import { useConfigContext } from "@providers/config"

const INITIAL_STATE = {
  name: "",
  email: "",
  phone: "",
  type: "",
  option: "",
  message: "",
  dropdownOptions: [],
}

export const useForm = () => {
  const {
    store: { locationLookupUrl },
  } = useConfigContext()
  const { callFunction } = useFunctions()
  const { sanityFetch } = useSanity()
  const [loading, setLoading] = useState(false)
  const [forms, setForms] = useState(null)
  const [errors, setErrors] = useState<Array<string>>([])
  const [data, setData] = useState(INITIAL_STATE)
  const query = `
    *[_type == "settingForms"] {
      _id,
      title,
      "handle": handle.current,
      subject,
      formFields[] {
        ...
      }, 
      formSuccessMessage
    }
  `

  const fetchForms = useCallback(async () => {
    setLoading(true)

    const sanityResults = await sanityFetch(query)

    setForms(sanityResults)

    setLoading(false)
  }, [query, sanityFetch])

  useEffect(() => {
    if (!forms?.length && !loading) {
      fetchForms()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchForms])

  const submitForm = useCallback(
    async (form, data) => {
      setLoading(true)
      setErrors([])

      const id = form?._id
      const type = form?.types?.[data?.type?.toLowerCase()]?.title.toLowerCase()
      const subject = form?.subject || `Form submission from ${form?.title}`
      const response = await fetch(locationLookupUrl)
      const userData = await response.json()
      const { status, body } = await callFunction("form-submission", {
        id,
        type,
        subject,
        data,
        ipData: userData,
        userAgent: navigator.userAgent || "",
      })

      if (status === "error") setErrors([body])
      if (status === "success") setData(INITIAL_STATE)

      setLoading(false)
    },
    [setLoading, setErrors, setData, locationLookupUrl, callFunction]
  )

  const getForm = useCallback(
    (handle?: string) =>
      forms
        ?.map(obj => {
          if (obj.handle === handle) {
            return obj
          }
          return null
        })
        .find(obj => obj !== null) || false,
    [forms]
  )

  const handleChange = ({ target: { type, name, value, checked } }: React.ChangeEvent<HTMLInputElement>) => {
    setData(prevState => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  return { getForm, submitForm, data, setData, handleChange, loading, errors }
}

export const useFocus = () => {
  const ref = useRef(null)
  const setFocus = () => {
    //@ts-ignore next-line
    ref?.current?.focus()
  }

  return [ref, setFocus]
}
